export interface TranslatedText {
  key: string;
  fallback: string;
}

export interface QuizOption {
  value: string;
  label: TranslatedText;
  next: string;
}

export interface ChoiceNode {
  type: "choice";
  id: string;
  title: TranslatedText;
  subtitle?: TranslatedText;
  options: QuizOption[];
}

export interface SchedulerNode {
  type: "scheduler";
  id: string;
  title: TranslatedText;
  subtitle?: TranslatedText;
  next: string;
}

export interface InfoNode {
  type: "info";
  id: string;
  title: TranslatedText;
  body?: TranslatedText;
  ctaLabel: TranslatedText;
  next: string;
}

export type QuizNode = ChoiceNode | SchedulerNode | InfoNode;

export const QUIZ_START_ID = "role";
export const QUIZ_DONE_ID = "done";

// Questions that are asked identically in several branches of the flow
// (kept as shared constants so the wording only lives in one place).
const T_UNTERSTUETZUNG: TranslatedText = {
  key: "onboardingQuiz.q.unterstuetzung.title",
  fallback: "Möchtest du Unterstützung bei der Planung von einem KNX-Experten?", // no itx
};
const T_KENNTNISSE: TranslatedText = {
  key: "onboardingQuiz.q.kenntnisse.title",
  fallback: "Wie würdest du deine KNX-Kenntnisse einschätzen?", // no itx
};
const T_SCHEMA: TranslatedText = {
  key: "onboardingQuiz.q.schema.title",
  fallback: "Welches Gruppenadressen-Schema möchtest du verwenden?", // no itx
};
const T_SCHEMA_SUBTITLE: TranslatedText = {
  key: "onboardingQuiz.q.schema.subtitle",
  fallback: "Falls du dir noch unsicher bist, ist das kein Problem – wir helfen dir dabei.", // no itx
};

function schemaOptions(next: string): QuizOption[] {
  return [
    // Reuses the same wording/keys as the group address scheme picker in SettingPageGroupAdresses.tsx.
    { value: "a", label: { key: "SettingsScreen.gAOptionA", fallback: "A: (Etage & Raum) / Funktion / Signal" }, next },
    {
      value: "b",
      label: { key: "SettingsScreen.gAOptionB", fallback: "B: (Etage & Raum) / Type / (Funktion & Signal)" },
      next,
    },
    { value: "c", label: { key: "SettingsScreen.gAOptionC", fallback: "C: Type / Etage / (Funktion & Signal)" }, next },
    { value: "other", label: { key: "onboardingQuiz.q.schema.other", fallback: "Ein anderes" }, next },
    { value: "unknown", label: { key: "onboardingQuiz.q.schema.unknown", fallback: "Ich weiß noch nicht" }, next },
  ];
}

function kenntnisseOptions(next: string): QuizOption[] {
  return [
    { value: "beginner", label: { key: "onboardingQuiz.q.kenntnisse.beginner", fallback: "Anfänger" }, next },
    {
      value: "advanced", // no itx
      label: { key: "onboardingQuiz.q.kenntnisse.advanced", fallback: "Fortgeschritten" },
      next,
    },
    { value: "expert", label: { key: "onboardingQuiz.q.kenntnisse.expert", fallback: "Experte" }, next },
  ];
}

function unterstuetzungOptions(yesNext: string, noNext: string, unknownNext: string): QuizOption[] {
  return [
    { value: "yes", label: { key: "onboardingQuiz.q.unterstuetzung.yes", fallback: "Ja" }, next: yesNext },
    { value: "no", label: { key: "onboardingQuiz.q.unterstuetzung.no", fallback: "Nein" }, next: noNext },
    {
      value: "unknown", // no itx
      label: { key: "onboardingQuiz.q.unterstuetzung.unknown", fallback: "Weiß noch nicht" },
      next: unknownNext,
    },
  ];
}

export const QUIZ_FLOW: Record<string, QuizNode> = {
  [QUIZ_START_ID]: {
    type: "choice",
    id: QUIZ_START_ID,
    title: { key: "onboardingQuiz.role.title", fallback: "Was beschreibt dich am besten?" },
    subtitle: {
      key: "onboardingQuiz.role.subtitle",
      fallback: "So können wir die App besser auf dich anpassen!", // no itx
    },
    options: [
      {
        value: "bauherr", // no itx
        label: { key: "onboardingQuiz.role.bauherr", fallback: "Bauherr" },
        next: "bauherr_zeit", // no itx
      },
      {
        value: "elektriker", // no itx
        label: { key: "onboardingQuiz.role.elektriker", fallback: "Elektriker" },
        next: "elektriker_unterstuetzung", // no itx
      },
      {
        value: "systemintegrator", // no itx
        label: { key: "onboardingQuiz.role.systemintegrator", fallback: "Systemintegrator" },
        next: "si_schema", // no itx
      },
      {
        value: "andere", // no itx
        label: { key: "onboardingQuiz.role.andere", fallback: "Etwas anderes" },
        next: "andere_projekt", // no itx
      },
    ],
  },

  // ---- Bauherr ----
  bauherr_zeit: {
    type: "choice",
    id: "bauherr_zeit",
    title: { key: "onboardingQuiz.bauherr.zeit.title", fallback: "Wann soll dein Projekt fertig sein?" },
    options: [
      {
        value: "1m", // no itx
        label: { key: "onboardingQuiz.bauherr.zeit.1m", fallback: "In 1 Monat" },
        next: "bauherr_unterstuetzung",
      },
      {
        value: "3-6m", // no itx
        label: { key: "onboardingQuiz.bauherr.zeit.3-6m", fallback: "In 3–6 Monaten" },
        next: "bauherr_unterstuetzung",
      },
      {
        value: "12-24m", // no itx
        label: { key: "onboardingQuiz.bauherr.zeit.12-24m", fallback: "In 12–24 Monaten" },
        next: "bauherr_unterstuetzung",
      },
      {
        value: "unknown", // no itx
        label: { key: "onboardingQuiz.bauherr.zeit.unknown", fallback: "Weiß noch nicht" },
        next: "bauherr_stehtfest", // no itx
      },
    ],
  },
  bauherr_stehtfest: {
    type: "choice",
    id: "bauherr_stehtfest",
    title: {
      key: "onboardingQuiz.bauherr.stehtfest.title",
      fallback: "Steht schon fest, dass mit KNX gebaut werden soll?", // no itx
    },
    options: [
      {
        value: "yes", // no itx
        label: { key: "onboardingQuiz.bauherr.stehtfest.yes", fallback: "Ja" },
        next: "bauherr_unterstuetzung",
      },
      {
        value: "no", // no itx
        label: { key: "onboardingQuiz.bauherr.stehtfest.no", fallback: "Nein" },
        next: "finish", // no itx
      },
    ],
  },
  bauherr_unterstuetzung: {
    type: "choice",
    id: "bauherr_unterstuetzung",
    title: T_UNTERSTUETZUNG,
    options: unterstuetzungOptions("scheduler", "finish", "finish"), // no itx
  },

  // ---- Elektriker ----
  elektriker_unterstuetzung: {
    type: "choice",
    id: "elektriker_unterstuetzung",
    title: T_UNTERSTUETZUNG,
    options: unterstuetzungOptions("scheduler", "elektriker_kenntnisse", "elektriker_kenntnisse"), // no itx
  },
  elektriker_kenntnisse: {
    type: "choice",
    id: "elektriker_kenntnisse",
    title: T_KENNTNISSE,
    options: kenntnisseOptions("elektriker_schema"), // no itx
  },
  elektriker_schema: {
    type: "choice",
    id: "elektriker_schema",
    title: T_SCHEMA,
    subtitle: T_SCHEMA_SUBTITLE,
    options: schemaOptions("finish"), // no itx
  },

  // ---- Systemintegrator ----
  si_schema: {
    type: "choice",
    id: "si_schema",
    title: T_SCHEMA,
    subtitle: T_SCHEMA_SUBTITLE,
    options: schemaOptions("si_anzahl"), // no itx
  },
  si_anzahl: {
    type: "choice",
    id: "si_anzahl",
    title: {
      key: "onboardingQuiz.si.anzahl.title",
      fallback: "Wie viele Projekte betreust du durchschnittlich im Jahr?", // no itx
    },
    options: [
      { value: "1-4", label: { key: "onboardingQuiz.si.anzahl.1-4", fallback: "1–4" }, next: "si_demo" },
      { value: "4-8", label: { key: "onboardingQuiz.si.anzahl.4-8", fallback: "4–8" }, next: "si_demo" },
      { value: "8-15", label: { key: "onboardingQuiz.si.anzahl.8-15", fallback: "8–15" }, next: "si_demo" },
      { value: "15+", label: { key: "onboardingQuiz.si.anzahl.15+", fallback: ">15" }, next: "si_demo" },
    ],
  },
  si_demo: {
    type: "choice",
    id: "si_demo",
    title: { key: "onboardingQuiz.si.demo.title", fallback: "Hättest du gerne einen persönlichen Demotermin?" },
    options: [
      { value: "yes", label: { key: "onboardingQuiz.si.demo.yes", fallback: "Ja" }, next: "scheduler" },
      { value: "no", label: { key: "onboardingQuiz.si.demo.no", fallback: "Nein" }, next: "finish" }, // no itx
      {
        value: "later", // no itx
        label: { key: "onboardingQuiz.si.demo.later", fallback: "Vielleicht später" },
        next: "finish", // no itx
      },
    ],
  },

  // ---- Etwas anderes ----
  andere_projekt: {
    type: "choice",
    id: "andere_projekt",
    title: { key: "onboardingQuiz.andere.projekt.title", fallback: "Hast du ein aktuelles KNX-Projekt?" },
    options: [
      {
        value: "yes", // no itx
        label: { key: "onboardingQuiz.andere.projekt.yes", fallback: "Ja" },
        next: "andere_kenntnisse",
      },
      {
        value: "browsing", // no itx
        label: { key: "onboardingQuiz.andere.projekt.browsing", fallback: "Ich will nur mal gucken" },
        next: "andere_kenntnisse",
      },
      {
        value: "unsure", // no itx
        label: { key: "onboardingQuiz.andere.projekt.unsure", fallback: "Noch unsicher gegenüber KNX" },
        next: "andere_kenntnisse",
      },
      {
        value: "old", // no itx
        label: { key: "onboardingQuiz.andere.projekt.old", fallback: "Ich habe ein altes KNX-Projekt" },
        next: "andere_kenntnisse",
      },
      {
        value: "other", // no itx
        label: { key: "onboardingQuiz.andere.projekt.other", fallback: "Etwas anderes" },
        next: "andere_kenntnisse",
      },
    ],
  },
  andere_kenntnisse: {
    type: "choice",
    id: "andere_kenntnisse",
    title: T_KENNTNISSE,
    options: kenntnisseOptions("andere_unterstuetzung"),
  },
  andere_unterstuetzung: {
    type: "choice",
    id: "andere_unterstuetzung",
    title: T_UNTERSTUETZUNG,
    options: unterstuetzungOptions("scheduler", "andere_schema", "andere_schema"), // no itx
  },
  andere_schema: {
    type: "choice",
    id: "andere_schema",
    title: T_SCHEMA,
    subtitle: T_SCHEMA_SUBTITLE,
    options: schemaOptions("finish"), // no itx
  },

  // ---- Shared terminal steps ----
  scheduler: {
    type: "scheduler",
    id: "scheduler",
    title: { key: "onboardingQuiz.scheduler.title", fallback: "Termin vereinbaren" },
    subtitle: {
      key: "onboardingQuiz.scheduler.subtitle",
      fallback: "Wähle einen Tag und eine Uhrzeit für dein Gespräch mit einem KNX-Experten.", // no itx
    },
    next: "finish", // no itx
  },
  finish: {
    type: "info",
    id: "finish",
    title: { key: "onboardingQuiz.finish.title", fallback: "Lass uns dein erstes Projekt erstellen:" },
    ctaLabel: { key: "onboardingQuiz.finish.cta", fallback: "Projekt erstellen" },
    next: QUIZ_DONE_ID,
  },
};

// Shown as an extra line above the regular "finish" title when the user booked
// an appointment with a KNX expert right before reaching this step.
export const FINISH_TITLE_AFTER_BOOKING: TranslatedText = {
  key: "onboardingQuiz.finish.titleAfterBooking",
  fallback: "Danke, wir schicken dir in Kürze eine E-Mail zur Bestätigung!", // no itx
};

const depthMemo = new Map<string, number>();

/** Longest remaining number of screens (including this one) until the quiz ends. */
export function getNodeDepth(id: string): number {
  if (id === QUIZ_DONE_ID) return 0;
  const cached = depthMemo.get(id);
  if (cached !== undefined) return cached;

  const node = QUIZ_FLOW[id];
  if (!node) return 0;

  const depth =
    node.type === "choice"
      ? 1 + Math.max(...node.options.map((option) => getNodeDepth(option.next)))
      : 1 + getNodeDepth(node.next);

  depthMemo.set(id, depth);
  return depth;
}
